import { useSelector } from 'react-redux';
import { InjectedFormProps, reduxForm } from 'redux-form';
import styled, { css } from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import EmptyRow from 'dashboard/layouts/FormLayout/EmptyRow';
import Form from 'dashboard/layouts/FormLayout/Form';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import { HStack } from 'design-system/components/Stack';
import { Paragraph } from 'design-system/components/Text';
import { TransitionDuration } from 'design-system/variables/transitions';
import FormControlField from 'lib/interfaces/FormControlField';
import { isRequired, withMessage } from 'lib/utils/formValidation';
import PinInputField from 'verify/components/FormControls/PinInputField';

const validate = {
  code: [withMessage('Please enter your authentication code', isRequired)],
};

const ResendLink = styled.a.attrs(() => ({ role: 'button' }))<{ disabled: boolean }>`
  cursor: pointer;
  transition-duration: ${TransitionDuration.Fast};

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      pointer-events: none;
      color: ${({ theme }) => theme.color.text.disabled};
    `}
`;

const DEFAULT_CODE_LENGTH = 6;

interface FormMfaCodeProps {
  type?: 'text' | 'pin';
  codeLength?: number;
  onCancel: () => void;
  onResend?: () => void;
  resendLoading?: boolean;
}

const FormMfaCode = ({
  handleSubmit,
  submitting,
  type = 'pin',
  codeLength = DEFAULT_CODE_LENGTH,
  onCancel,
  onResend,
  resendLoading,
}: InjectedFormProps<FormMfaCodeData> & FormMfaCodeProps) => {
  const givenCodeLength = useSelector<RootState, number>(
    (state) => state.form['mfa-code']?.values?.code.length ?? 0,
  );

  return (
    <Form onSubmit={handleSubmit}>
      {type === 'text' ? (
        <FormControlField
          label=''
          component={InputField}
          name='code'
          id='code'
          disabled={submitting}
          validate={validate.code}
          autoFocus={true}
        />
      ) : (
        <FormControlField
          label=''
          component={PinInputField}
          name='code'
          id='code'
          disabled={submitting}
          validate={validate.code}
          autoFocus={true}
          codeLength={codeLength}
        />
      )}
      {onResend != null && (
        <>
          <EmptyRow />
          <Paragraph>
            {"Didn't receive a code?"}{' '}
            <ResendLink
              id='form-mfa-code__btn--resend'
              data-test='form-mfa-code__btn--resend'
              onClick={onResend}
              disabled={resendLoading || submitting}
            >
              Try again.
            </ResendLink>
          </Paragraph>
        </>
      )}
      <EmptyRow rows={2} />
      <HStack equal={true}>
        <Button
          variant='basic'
          id='form-mfa-code__btn--cancel'
          data-test='form-mfa-code__btn--cancel'
          onClick={onCancel}
          disabled={submitting}
        >
          Back
        </Button>
        <Button
          type='submit'
          id='form-mfa-code__btn--confirm'
          data-test='form-mfa-code__btn--confirm'
          disabled={submitting || givenCodeLength < codeLength}
          isPending={submitting}
        >
          Confirm
        </Button>
      </HStack>
    </Form>
  );
};

export interface FormMfaCodeData {
  code: string;
}

/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
export default reduxForm<FormMfaCodeData, FormMfaCodeProps>({ form: 'mfa-code' })(FormMfaCode);
